import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { DateFormatPattern, WeekNumbering } from '../api/types';
import { useAuth } from '../contexts/AuthContext';
import { ConnectorStyle, loadConnectorStyle, saveConnectorStyle } from '../utils/connectorStyle';
import { formatDate } from '../utils/dateFormat';

const DATE_FORMATS: DateFormatPattern[] = ['DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY'];

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const { user, refresh } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connectorStyle, setConnectorStyle] = useState<ConnectorStyle>(loadConnectorStyle);

  const handleConnectorStyleChange = (style: ConnectorStyle) => {
    setConnectorStyle(style);
    saveConnectorStyle(style);
  };

  const handleLanguageChange = async (lang: string) => {
    await i18n.changeLanguage(lang);
    await api.patch('/users/me/preferences', { preferredLanguage: lang });
    await refresh();
  };

  const handleDateFormatChange = async (dateFormat: DateFormatPattern) => {
    await api.patch('/users/me/preferences', { dateFormat });
    await refresh();
  };

  const handleWeekNumberingChange = async (weekNumbering: WeekNumbering) => {
    await api.patch('/users/me/preferences', { weekNumbering });
    await refresh();
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setMessage(t('settings.passwordUpdated'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  return (
    <div>
      <h2>{t('settings.title')}</h2>

      <div className="card" style={{ maxWidth: 420, marginBottom: 20 }}>
        <div className="form-field">
          <label>{t('settings.language')}</label>
          <select value={i18n.language.startsWith('en') ? 'en' : 'fr'} onChange={(e) => handleLanguageChange(e.target.value)}>
            <option value="fr">Français</option>
            <option value="en">English</option>
          </select>
        </div>
        <div className="form-field">
          <label>{t('settings.connectorStyle')}</label>
          <select value={connectorStyle} onChange={(e) => handleConnectorStyleChange(e.target.value as ConnectorStyle)}>
            <option value="curved">{t('settings.connectorCurved')}</option>
            <option value="orthogonal">{t('settings.connectorOrthogonal')}</option>
          </select>
        </div>
        <div className="form-field">
          <label>{t('settings.dateFormat')}</label>
          <select
            value={user?.dateFormat ?? 'DD-MM-YYYY'}
            onChange={(e) => handleDateFormatChange(e.target.value as DateFormatPattern)}
          >
            {DATE_FORMATS.map((f) => (
              <option key={f} value={f}>
                {f} ({formatDate(new Date().toISOString(), f)})
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label>{t('settings.weekNumbering')}</label>
          <select value={user?.weekNumbering ?? 'ISO'} onChange={(e) => handleWeekNumberingChange(e.target.value as WeekNumbering)}>
            <option value="ISO">{t('settings.weekNumberingIso')}</option>
            <option value="US">{t('settings.weekNumberingUs')}</option>
          </select>
        </div>
      </div>

      {user?.authProvider === 'LOCAL' && (
        <div className="card" style={{ maxWidth: 420 }}>
          <h4 style={{ marginTop: 0 }}>{t('settings.changePassword')}</h4>
          <form onSubmit={handleChangePassword}>
            <div className="form-field">
              <label>{t('settings.currentPassword')}</label>
              <input type="password" required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </div>
            <div className="form-field">
              <label>{t('settings.newPassword')}</label>
              <input type="password" required minLength={10} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            {error && <p className="error-text">{error}</p>}
            {message && <p className="success-text">{message}</p>}
            <button type="submit" className="btn btn-primary">
              {t('settings.changePasswordButton')}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
